/**
 * Date and time utilities
 * Common date/time operations for all tools
 */
class DateTimeUtils {
    /**
     * Format date for input element
     * @param {Date} date - Date object
     * @returns {string} Formatted date string (YYYY-MM-DD)
     */
    static formatDateForInput(date) {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    }

    /**
     * Format date for display
     * @param {Date} date - Date object
     * @param {string} format - Format string
     * @returns {string} Formatted date string
     */
    static formatDate(date, format = 'YYYY-MM-DD HH:mm:ss') {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const seconds = String(date.getSeconds()).padStart(2, '0');

        return format
            .replace('YYYY', year)
            .replace('MM', month)
            .replace('DD', day)
            .replace('HH', hours)
            .replace('mm', minutes)
            .replace('ss', seconds);
    }

    /**
     * Parse log timestamp
     * @param {string} timestamp - Timestamp string
     * @returns {Date} Parsed date object
     */
    static parseLogTimestamp(timestamp) {
        // Handle different timestamp formats
        const patterns = [
            /(\d{2})-([A-Za-z]{3})-(\d{2}) (\d{2}):(\d{2}):(\d{2})/,
            /(\d{4})-(\d{2})-(\d{2}) (\d{2}):(\d{2}):(\d{2})/,
            /(\d{2})\/(\d{2})\/(\d{4}) (\d{2}):(\d{2}):(\d{2})/
        ];

        const monthMap = {
            'Jan': 0, 'Feb': 1, 'Mar': 2, 'Apr': 3, 'May': 4, 'Jun': 5,
            'Jul': 6, 'Aug': 7, 'Sep': 8, 'Oct': 9, 'Nov': 10, 'Dec': 11
        };

        for (const pattern of patterns) {
            const match = timestamp.match(pattern);
            if (match) {
                if (pattern.source.includes('A-Za-z')) {
                    // DD-Mon-YY format
                    const [, day, month, year, hour, minute, second] = match;
                    return new Date(2000 + parseInt(year), monthMap[month], parseInt(day), 
                                   parseInt(hour), parseInt(minute), parseInt(second));
                } else {
                    // Standard formats
                    const [, year, month, day, hour, minute, second] = match;
                    return new Date(parseInt(year), parseInt(month) - 1, parseInt(day),
                                   parseInt(hour), parseInt(minute), parseInt(second));
                }
            }
        }

        return new Date(timestamp);
    }

    /**
     * Check if date is within range
     * @param {Date} date - Date to check
     * @param {Date} startDate - Start date
     * @param {Date} endDate - End date
     * @returns {boolean} True if date is within range
     */
    static isDateInRange(date, startDate, endDate) {
        return date >= startDate && date <= endDate;
    }

    /**
     * Get date range presets
     * @returns {Object} Object containing date range presets
     */
    static getDateRangePresets() {
        const today = new Date();
        const yesterday = new Date(today);
        yesterday.setDate(today.getDate() - 1);
        
        const lastWeek = new Date(today);
        lastWeek.setDate(today.getDate() - 7);
        
        const lastMonth = new Date(today);
        lastMonth.setMonth(today.getMonth() - 1);

        return {
            today: { start: today, end: today },
            yesterday: { start: yesterday, end: yesterday },
            lastWeek: { start: lastWeek, end: today },
            lastMonth: { start: lastMonth, end: today }
        };
    }

    /**
     * Create timestamp for filename
     * @returns {string} Timestamp string for filename
     */
    static createTimestampForFilename() {
        const now = new Date();
        return this.formatDate(now, 'YYYY-MM-DD_HH-mm-ss');
    }
}

// Export for module usage
if (typeof module !== 'undefined' && module.exports) {
    module.exports = DateTimeUtils;
}
